import { Group, iconSizes, Loader, Stack, Text, Tooltip } from '@common/ui'
import { Anchor } from '@common/ui/mantine'
import { IconUnlink } from '@common/ui/icons'
import { com } from '@eidu/web'
import React from 'react'
import EntityId = com.eidu.entity.EntityId
import EntityTypeId = com.eidu.entity.type.EntityTypeId

export type EntityReferenceProps = {
  id: EntityId | undefined
  typeId: EntityTypeId | undefined
  label?: string
  secondaryLabel?: string
  labelAsLink?: boolean
  typeName?: string
  state?: 'resolved' | 'unresolved' | 'pending'
}

const EntityReference = ({
  id,
  typeId,
  label,
  secondaryLabel,
  labelAsLink = true,
  typeName,
  state = 'resolved',
}: EntityReferenceProps) => (
  <Tooltip
    multiline
    maw={320}
    label={
      <>
        {typeName && <div style={{ fontStyle: 'italic', fontWeight: 700 }}>{typeName}</div>}
        <div>
          {state === 'unresolved' && 'The referenced entity could not be found: '}
          {state === 'pending' && 'Loading '}
          {id?.toString() ?? '?'}
        </div>
        {state === 'resolved' && !label && (
          <div style={{ marginTop: 8 }}>
            Showing the entity&apos;s internal identifier because we could not find a more suitable label.
          </div>
        )}
      </>
    }
  >
    <Stack gap={0} style={{ minWidth: 0, overflow: 'hidden' }}>
      {state === 'resolved' && label && (
        <>
          {typeId && id && labelAsLink ? (
            <Anchor
              underline="hover"
              c="platform"
              href={`/entities/${typeId.toString()}/view/${id.toString()}`}
              aria-current="page"
              onClick={(event): void => event.stopPropagation()}
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
            >
              {label}
            </Anchor>
          ) : (
            <Text c="platform" truncate>
              {label}
            </Text>
          )}
          {secondaryLabel && (
            <Text size="sm" c="dimmed" truncate>
              {secondaryLabel}
            </Text>
          )}
        </>
      )}
      {state === 'resolved' && !label && (
        <Text c="platform" truncate fs="italic">
          {id?.toString() ?? '?'}
        </Text>
      )}
      {state === 'unresolved' && (
        <Group gap="xxs" wrap="nowrap" style={{ minWidth: 0, overflow: 'hidden' }}>
          <IconUnlink
            size={iconSizes.sm}
            stroke={1.5}
            color="var(--trellis-color-negative)"
            style={{ flexShrink: 0 }}
          />
          <Text c="negative" truncate fs="italic">
            {id?.toString() ?? '?'}
          </Text>
        </Group>
      )}
      {state === 'pending' && <Loader size="sm" />}
    </Stack>
  </Tooltip>
)

export default EntityReference
